<template>
  <BaseReportTable
    :resultados="resultados"
    :total-items="totalItems"
    :items-por-pagina="itemsPorPagina"
    :pagina-actual="paginaActual"
    :loading="loading"
    :headers="financiamientoHeaders"
    :es-operacion="esOperacion"
    :item-class="getRowClass"
    @cambiar-pagina="$emit('cambiar-pagina', $event)"
    @cambiar-items-por-pagina="$emit('cambiar-items-por-pagina', $event)"
  >
    <!-- Template condicional para filas de subtotales -->
    <template v-slot:item.id="{ item }">
      <template v-if="item._esSubtotal">
        <strong class="subtotal-label">📊 SUBTOTAL</strong>
      </template>
      <template v-else>
        {{ item.id }}
      </template>
    </template>

    <template v-slot:item.legajo="{ item }">
      <template v-if="item._esSubtotal">
        <!-- Vacío para subtotales -->
      </template>
      <template v-else>
        <a
          v-if="item.legajo"
          :href="getGestionarUrl(item.legajo)"
          target="_blank"
          class="legajo-link"
        >
          {{ item.legajo }}
        </a>
        <template v-else>-</template>
      </template>
    </template>

    <template v-slot:item.fideicomiso="{ item }">
      <template v-if="item._esSubtotal">
        <strong class="subtotal-label">📈 {{ item._cantidadRegistros }} registros</strong>
      </template>
      <template v-else>
        {{ item.fideicomiso || '-' }}
      </template>
    </template>

    <template v-slot:item.operatoria="{ item }">
      <template v-if="item._esSubtotal">
        <strong class="subtotal-label">🎯 {{ item._subtotales.operatoria || item.operatoria_linea }}</strong>
      </template>
      <template v-else>
        {{ item.operatoria_linea || '-' }}
      </template>
    </template>

    <template v-slot:item.cliente="{ item }">
      <template v-if="!item._esSubtotal">
        {{
          item.cliente.razonSocial ||
          (item.cliente.nombre + ' ' + item.cliente.apellido).trim() ||
          '-'
        }}
      </template>
    </template>

    <template v-slot:item.cuit="{ item }">
      <template v-if="!item._esSubtotal">
        {{ item.cliente.cuit || '-' }}
      </template>
    </template>

    <template v-slot:item.persona="{ item }">
      <template v-if="!item._esSubtotal">
        <v-chip
          small
          :color="item.cliente.tipoCliente === 1 ? 'blue' : 'purple'"
          text-color="white"
        >
          {{ item.cliente.tipoCliente === 0 ? 'Jurídica' : 'Física' }}
        </v-chip>
      </template>
    </template>

    <template v-slot:item.fechaIngreso="{ item }">
      <template v-if="!item._esSubtotal">
        {{ formatDates(item.fechaIngreso) }}
      </template>
    </template>

    <template v-slot:item.estado="{ item }">
      <template v-if="!item._esSubtotal">
        <v-chip
          small
          :color="obtenerColorEstado(item.estado)"
          text-color="white"
        >
          {{ item.estado || '-' }}
        </v-chip>
      </template>
    </template>

    <!-- Templates específicos para Financiamiento -->
    <template v-slot:item.monto="{ item }">
      <template v-if="item._esSubtotal">
        <strong class="subtotal-value">${{ formatoNumero(getSubtotalMontoOtorgado(item)) }}</strong>
      </template>
      <template v-else>
        ${{ formatoNumero(getMontoOtorgadoConValidacion(item, 'monto')) }}
      </template>
    </template>

    <template v-slot:item.fechaOtorgamiento="{ item }">
      <template v-if="!item._esSubtotal">
        {{ item.fechaOtorgamiento || '-' }}
      </template>
    </template>

    <template v-slot:item.monto_aprobado="{ item }">
      <template v-if="item._esSubtotal">
        <strong class="subtotal-value">${{ formatoNumero(getSubtotalMontoAprobado(item)) }}</strong>
      </template>
      <template v-else>
        ${{ formatoNumero(getTipoMonto(item.parametros, 'monto_aprobado') || item.monto_aprobado || 0) }}
      </template>
    </template>

    <template v-slot:item.monto_pendiente="{ item }">
      <template v-if="item._esSubtotal">
        <strong class="subtotal-value">${{ formatoNumero(getSubtotalMontoPendiente(item)) }}</strong>
      </template>
      <template v-else>
        ${{ formatoNumero(getPendienteDesembolso(item)) }}
      </template>
    </template>

    <!-- Templates para desembolsos de financiamiento -->
    <template v-slot:item.desembolso_1="{ item }">
      <template v-if="item._esSubtotal">
        <strong class="subtotal-value">${{ formatoNumero(getTotalDesembolsoSubtotal(item, 1)) }}</strong>
      </template>
      <template v-else-if="esEstadoValidoParaMonto(item) && getDesembolso(item, 1)">
        <div class="desembolso-column">
          <div class="desembolso-monto">
            ${{ formatoNumero(getDesembolso(item, 1).monto) }}
          </div>
        </div>
      </template>
      <template v-else>
        <div class="text-center">-</div>
      </template>
    </template>

    <template v-slot:item.fecha_desembolso_1="{ item }">
      <template v-if="!item._esSubtotal">
        <div
          v-if="getDesembolso(item, 1) && getDesembolso(item, 1).fecha_desembolso"
          class="desembolso-fecha"
        >
          {{ getDesembolso(item, 1).fecha_desembolso }}
        </div>
        <div v-else class="text-center">-</div>
      </template>
    </template>

    <template v-slot:item.desembolso_2="{ item }">
      <template v-if="item._esSubtotal">
        <strong class="subtotal-value">${{ formatoNumero(getTotalDesembolsoSubtotal(item, 2)) }}</strong>
      </template>
      <template v-else-if="esEstadoValidoParaMonto(item) && getDesembolso(item, 2)">
        <div class="desembolso-column">
          <div class="desembolso-monto">
            ${{ formatoNumero(getDesembolso(item, 2).monto) }}
          </div>
        </div>
      </template>
      <template v-else>
        <div class="text-center">-</div>
      </template>
    </template>

    <template v-slot:item.fecha_desembolso_2="{ item }">
      <template v-if="!item._esSubtotal">
        <div
          v-if="getDesembolso(item, 2) && getDesembolso(item, 2).fecha_desembolso"
          class="desembolso-fecha"
        >
          {{ getDesembolso(item, 2).fecha_desembolso }}
        </div>
        <div v-else class="text-center">-</div>
      </template>
    </template>

    <template v-slot:item.desembolso_3="{ item }">
      <template v-if="item._esSubtotal">
        <strong class="subtotal-value">${{ formatoNumero(getTotalDesembolsoSubtotal(item, 3)) }}</strong>
      </template>
      <template v-else-if="esEstadoValidoParaMonto(item) && getDesembolso(item, 3)">
        <div class="desembolso-column">
          <div class="desembolso-monto">
            ${{ formatoNumero(getDesembolso(item, 3).monto) }}
          </div>
        </div>
      </template>
      <template v-else>
        <div class="text-center">-</div>
      </template>
    </template>

    <template v-slot:item.fecha_desembolso_3="{ item }">
      <template v-if="!item._esSubtotal">
        <div
          v-if="getDesembolso(item, 3) && getDesembolso(item, 3).fecha_desembolso"
          class="desembolso-fecha"
        >
          {{ getDesembolso(item, 3).fecha_desembolso }}
        </div>
        <div v-else class="text-center">-</div>
      </template>
    </template>

    <template v-slot:item.desembolso_4="{ item }">
      <template v-if="item._esSubtotal">
        <strong class="subtotal-value">${{ formatoNumero(getTotalDesembolsoSubtotal(item, 4)) }}</strong>
      </template>
      <template v-else-if="esEstadoValidoParaMonto(item) && getDesembolso(item, 4)">
        <div class="desembolso-column">
          <div class="desembolso-monto">
            ${{ formatoNumero(getDesembolso(item, 4).monto) }}
          </div>
        </div>
      </template>
      <template v-else>
        <div class="text-center">-</div>
      </template>
    </template>

    <template v-slot:item.fecha_desembolso_4="{ item }">
      <template v-if="!item._esSubtotal">
        <div
          v-if="getDesembolso(item, 4) && getDesembolso(item, 4).fecha_desembolso"
          class="desembolso-fecha"
        >
          {{ getDesembolso(item, 4).fecha_desembolso }}
        </div>
        <div v-else class="text-center">-</div>
      </template>
    </template>

    <template v-slot:item.desembolso_5="{ item }">
      <template v-if="item._esSubtotal">
        <strong class="subtotal-value">${{ formatoNumero(getTotalDesembolsoSubtotal(item, 5)) }}</strong>
      </template>
      <template v-else-if="esEstadoValidoParaMonto(item) && getDesembolso(item, 5)">
        <div class="desembolso-column">
          <div class="desembolso-monto">
            ${{ formatoNumero(getDesembolso(item, 5).monto) }}
          </div>
        </div>
      </template>
      <template v-else>
        <div class="text-center">-</div>
      </template>
    </template>

    <template v-slot:item.fecha_desembolso_5="{ item }">
      <template v-if="!item._esSubtotal">
        <div
          v-if="getDesembolso(item, 5) && getDesembolso(item, 5).fecha_desembolso"
          class="desembolso-fecha"
        >
          {{ getDesembolso(item, 5).fecha_desembolso }}
        </div>
        <div v-else class="text-center">-</div>
      </template>
    </template>
    <template v-slot:item.desembolso_6="{ item }">
      <template v-if="item._esSubtotal">
        <strong class="subtotal-value">${{ formatoNumero(getTotalDesembolsoSubtotal(item, 6)) }}</strong>
      </template>
      <template v-else-if="esEstadoValidoParaMonto(item) && getDesembolso(item, 6)">
        <div class="desembolso-column">
          <div class="desembolso-monto">
            ${{ formatoNumero(getDesembolso(item, 6).monto) }}
          </div>
        </div>
      </template>
      <template v-else>
        <div class="text-center">-</div>
      </template>
    </template>

    <template v-slot:item.fecha_desembolso_6="{ item }">
      <template v-if="!item._esSubtotal">
        <div
          v-if="getDesembolso(item, 6) && getDesembolso(item, 6).fecha_desembolso"
          class="desembolso-fecha"
        >
          {{ getDesembolso(item, 6).fecha_desembolso }}
        </div>
        <div v-else class="text-center">-</div>
      </template>
    </template>
    <template v-slot:item.desembolso_7="{ item }">
      <template v-if="item._esSubtotal">
        <strong class="subtotal-value">${{ formatoNumero(getTotalDesembolsoSubtotal(item, 7)) }}</strong>
      </template>
      <template v-else-if="esEstadoValidoParaMonto(item) && getDesembolso(item, 7)">
        <div class="desembolso-column">
          <div class="desembolso-monto">
            ${{ formatoNumero(getDesembolso(item, 7).monto) }}
          </div>
        </div>
      </template>
      <template v-else>
        <div class="text-center">-</div>
      </template>
    </template>

    <template v-slot:item.fecha_desembolso_7="{ item }">
      <template v-if="!item._esSubtotal">
        <div
          v-if="getDesembolso(item, 7) && getDesembolso(item, 7).fecha_desembolso"
          class="desembolso-fecha"
        >
          {{ getDesembolso(item, 7).fecha_desembolso }}
        </div>
        <div v-else class="text-center">-</div>
      </template>
    </template>

    <!-- Templates para gastos específicos -->
    <template v-slot:item.honorarios_mf="{ item }">
      <template v-if="item._esSubtotal">
        <strong class="subtotal-value">${{ formatoNumero(getSubtotalGastoHonorariosMf(item)) }}</strong>
      </template>
      <template v-else>
        <div v-if="item.gastos && item.gastos.honorarios_mf > 0" class="text-center">
          ${{ formatoNumero(item.gastos.honorarios_mf) }}
        </div>
        <div v-else class="text-center">-</div>
      </template>
    </template>

    <template v-slot:item.gastos_escribano="{ item }">
      <template v-if="item._esSubtotal">
        <strong class="subtotal-value">${{ formatoNumero(getSubtotalGastoEscribano(item)) }}</strong>
      </template>
      <template v-else>
        <div v-if="item.gastos && item.gastos.gastos_escribano > 0" class="text-center">
          ${{ formatoNumero(item.gastos.gastos_escribano) }}
        </div>
        <div v-else class="text-center">-</div>
      </template>
    </template>

    <template v-slot:item.otros_gastos="{ item }">
      <template v-if="item._esSubtotal">
        <strong class="subtotal-value">${{ formatoNumero(getSubtotalOtrosGastos(item)) }}</strong>
      </template>
      <template v-else>
        <div v-if="item.gastos && item.gastos.otros_gastos > 0" class="text-center">
          ${{ formatoNumero(item.gastos.otros_gastos) }}
        </div>
        <div v-else class="text-center">-</div>
      </template>
    </template>

    <!-- Template para Monto Liquidado -->
    <template v-slot:item.liquidado="{ item }">
      <template v-if="item._esSubtotal">
        <strong class="subtotal-value">${{ formatoNumero(item._subtotales.gastos.liquidado || 0) }}</strong>
      </template>
      <template v-else>
        <div class="text-center">
          ${{ formatoNumero(getMontoLiquidado(item)) }}
        </div>
      </template>
    </template>

    <!-- Slot personalizado para totales de Financiamiento -->
    <template v-slot:totals-row="{ totals }">
      <!-- BaseReportTable ya incluye 8 columnas base (Legajo hasta Estado) -->
      <!-- El slot comienza en la columna 9 (Monto Aprobado) -->

      <!-- Para Financiamiento normal: Monto Aprobado + Monto Otorgado + Monto Pendiente -->
      <template v-if="esOperacion === 'Financiamiento'">
        <!-- Total Monto Aprobado (columna 9) -->
        <td>
          <strong class="totals-text">$ {{ formatoNumero(totals.totalMontoAprobado || 0) }}</strong>
        </td>

        <!-- Total Monto Otorgado (columna 10) -->
        <td>
          <strong class="totals-text">$ {{ formatoNumero(totals.totalMonto || 0) }}</strong>
        </td>

        <!-- Total Monto Pendiente (columna 11) -->
        <td>
          <strong class="totals-text">$ {{ formatoNumero(totals.totalMontoPendiente || 0) }}</strong>
        </td>
      </template>

      <!-- Para reportes especiales (Info Cobranzas, etc): Sin columnas de monto aprobado/pendiente -->
      <template v-else>
        <!-- Total Monto Otorgado (sin Aprobado ni Pendiente) -->
        <td>
          <strong class="totals-text">$ {{ formatoNumero(totals.totalMonto || 0) }}</strong>
        </td>
      </template>

      <!-- Fecha de otorgamiento (vacía para totales) -->
      <td></td>

      <!-- PRIMERO: Totales de gastos (para alinearse con los headers) -->
      <td>
        <strong class="totals-text">$ {{ formatoNumero(totals.totalGastoAdministrativo || 0) }}</strong>
      </td>
      <td>
        <strong class="totals-text">$ {{ formatoNumero(totals.totalGastoOtorgamiento || 0) }}</strong>
      </td>
      <td>
        <strong class="totals-text">$ {{ formatoNumero(totals.totalGastoIibb || 0) }}</strong>
      </td>

      <!-- SEGUNDO: Totales dinámicos de desembolsos (monto y fecha por cada desembolso) -->
      <template v-for="i in maxDesembolsos">
        <td :key="`desembolso-${i}`">
          <strong class="totals-text">$ {{ formatoNumero(getTotalDesembolso(i)) }}</strong>
        </td>
        <td :key="`fecha-${i}`"></td>
      </template>

      <!-- Total Monto Liquidado -->
      <td v-if="shouldIncludeGastos">
        <strong class="totals-text">$ {{ formatoNumero(getTotalMontoLiquidado()) }}</strong>
      </td>
    </template>
  </BaseReportTable>
</template>

<script>
import BaseReportTable from './BaseReportTable.vue';
import ReportCalculations from '../ReportCalculations';

export default {
  name: 'FinanciamientoReportTable',
  components: {
    BaseReportTable,
  },
  mixins: [ReportCalculations],
  props: {
    resultados: {
      type: Array,
      default: () => [],
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    itemsPorPagina: {
      type: Number,
      default: 10,
    },
    paginaActual: {
      type: Number,
      default: 1,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    headers: {
      type: Array,
      default: () => [],
    },
    esOperacion: {
      type: String,
      default: 'Financiamiento',
    },
  },
  emits: ['cambiar-pagina', 'cambiar-items-por-pagina'],
  computed: {
    financiamientoHeaders() {
      const baseHeaders = [...this.headers];

      // PRIMERO: Agregar columnas de gastos con nombres específicos
      // SIEMPRE incluir estas columnas para reportes de Financiamiento
      const gastosHeaders = [
        { text: 'Honorarios MF', value: 'honorarios_mf' },
        { text: 'Gastos de Escribano', value: 'gastos_escribano' },
        { text: 'Otros Gastos', value: 'otros_gastos' },
      ];

      gastosHeaders.forEach(gastoHeader => {
        // Solo agregar si no existe ya en baseHeaders
        if (!baseHeaders.some(h => h.value === gastoHeader.value)) {
          baseHeaders.push({
            text: gastoHeader.text,
            value: gastoHeader.value,
            sortable: false,
          });
        }
      });

      // SEGUNDO: Agregar columnas dinámicas de desembolsos para Financiamiento
      for (let i = 1; i <= this.maxDesembolsos; i++) {
        baseHeaders.push({
          text: `Desembolso ${i}`,
          value: `desembolso_${i}`,
          sortable: false,
        });
        baseHeaders.push({
          text: `Fecha Desembolso ${i}`,
          value: `fecha_desembolso_${i}`,
          sortable: false,
        });
      }

      // TERCERO: Agregar Monto Liquidado al final
      if (!baseHeaders.some(h => h.value === 'liquidado')) {
        baseHeaders.push({
          text: 'Monto Liquidado',
          value: 'liquidado',
          sortable: false,
        });
      }

      return baseHeaders;
    },
    incluyeMontoAprobado() {
      return this.headers.some((header) => header.value === 'monto_aprobado');
    },
    shouldIncludeGastos() {
      return this.resultados.some((item) => item.gastos && !item._esSubtotal);
    },
    incluyeMontoLiquidado() {
      return this.headers.some((header) => header.value === 'liquidado');
    },
  },
  methods: {
    getGestionarUrl(legajo) {
      const baseUrl = process.env.VUE_APP_GESTIONAR_URL || 'http://localhost:8080';
      return `${baseUrl}/gestionar/${legajo}`;
    },
    obtenerColorEstado(estado) {
      const colores = {
        Otorgado: 'green',
        Aprobado: 'blue',
        Análisis: 'orange',
        Rechazado: 'red',
        Cancelado: 'grey',
        Desistido: 'brown',
      };
      return colores[estado] || 'grey';
    },
    getMontoLiquidado(item) {
      // Solo calcular monto liquidado si el estado es "Otorgado" o "Cancelado"
      if (!this.esEstadoValidoParaMonto(item)) {
        return 0;
      }

      // Si hay desembolsos, calcular como suma de (monto + gastos) de cada desembolso
      if (item.desembolsos && Array.isArray(item.desembolsos) && item.desembolsos.length > 0) {
        return item.desembolsos.reduce((total, desembolso) => {
          const monto = parseFloat(desembolso.monto) || 0;
          const gastos = parseFloat(desembolso.gastos) || 0;
          return total + monto + gastos;
        }, 0);
      }

      // Si no hay desembolsos, usar el monto del parametro menos los gastos totales
      let monto = 0;
      if (item.parametros && item.parametros.monto && item.parametros.monto[0]) {
        monto = parseFloat(item.parametros.monto[0]) || 0;
      }

      let totalGastos = 0;
      if (item.gastos && item.gastos !== null && typeof item.gastos === 'object') {
        if (item.gastos.total) {
          totalGastos = parseFloat(item.gastos.total) || 0;
        }
      }

      return monto - totalGastos;
    },
    getTotalMontoLiquidado() {
      return this.resultados.reduce((total, item) => {
        if (item._esSubtotal) return total;
        const montoLiquidado = this.getMontoLiquidado(item);
        return total + montoLiquidado;
      }, 0);
    },
    getTotalDesembolsoSubtotal(item, desembolsoNum) {
      // Para filas de subtotal, buscar el total de desembolsos en la estructura _subtotales
      if (item._subtotales && item._subtotales.desembolsos && item._subtotales.desembolsos[desembolsoNum]) {
        return item._subtotales.desembolsos[desembolsoNum];
      }
      // Fallback: devolver 0 si no hay datos de subtotal para este desembolso
      return 0;
    },
    getSubtotalMontoOtorgado(subtotalItem) {
      // Si viene del backend y tiene valor, usarlo
      if (subtotalItem._subtotales && subtotalItem._subtotales.monto && subtotalItem._subtotales.monto > 0) {
        return subtotalItem._subtotales.monto;
      }

      // Si no, calcular sumando los items de la misma operatoria con estado válido
      let operatoriaNombre = '';
      if (subtotalItem._subtotales && subtotalItem._subtotales.operatoria) {
        operatoriaNombre = subtotalItem._subtotales.operatoria;
      } else if (subtotalItem.operatoria) {
        operatoriaNombre = subtotalItem.operatoria.replace('Subtotal ', '');
      }

      return this.resultados
        .filter(item => !item._esSubtotal && item.operatoria === operatoriaNombre)
        .reduce((total, item) => {
          // Solo sumar si el estado es válido
          if (!this.esEstadoValidoParaMonto(item)) {
            return total;
          }
          const montoOtorgado = this.getTipoMonto(item.parametros, 'monto') || 0;
          return total + parseFloat(montoOtorgado);
        }, 0);
    },
    getSubtotalMontoAprobado(subtotalItem) {
      // Si viene del backend y tiene valor, usarlo
      if (subtotalItem._subtotales && subtotalItem._subtotales.montoAprobado && subtotalItem._subtotales.montoAprobado > 0) {
        return subtotalItem._subtotales.montoAprobado;
      }

      // Si no, calcular sumando los items de la misma operatoria
      let operatoriaNombre = '';
      if (subtotalItem._subtotales && subtotalItem._subtotales.operatoria) {
        operatoriaNombre = subtotalItem._subtotales.operatoria;
      } else if (subtotalItem.operatoria) {
        operatoriaNombre = subtotalItem.operatoria.replace('Subtotal ', '');
      }

      return this.resultados
        .filter(item => !item._esSubtotal && item.operatoria === operatoriaNombre)
        .reduce((total, item) => {
          const montoAprobado = this.getTipoMonto(item.parametros, 'monto_aprobado') || item.monto_aprobado || 0;
          return total + parseFloat(montoAprobado);
        }, 0);
    },
    getSubtotalMontoPendiente(subtotalItem) {
      // Si viene del backend y tiene valor, usarlo
      if (subtotalItem._subtotales && subtotalItem._subtotales.montoPendiente && subtotalItem._subtotales.montoPendiente > 0) {
        return subtotalItem._subtotales.montoPendiente;
      }

      // Si no, calcular sumando los items de la misma operatoria
      let operatoriaNombre = '';
      if (subtotalItem._subtotales && subtotalItem._subtotales.operatoria) {
        operatoriaNombre = subtotalItem._subtotales.operatoria;
      } else if (subtotalItem.operatoria) {
        operatoriaNombre = subtotalItem.operatoria.replace('Subtotal ', '');
      }

      return this.resultados
        .filter(item => !item._esSubtotal && item.operatoria === operatoriaNombre)
        .reduce((total, item) => {
          const montoPendiente = this.getPendienteDesembolso(item);
          return total + parseFloat(montoPendiente || 0);
        }, 0);
    },
    getRowClass(item) {
      return item._esSubtotal ? 'subtotal-row' : '';
    },
    getGastoAdministrativo(item) {
      const gastoAdmin = item.gastos && item.gastos.administrativo ? parseFloat(item.gastos.administrativo) : 0;
      const honorariosMf = item.gastos && item.gastos.honorarios_mf ? parseFloat(item.gastos.honorarios_mf) : 0;
      return gastoAdmin + honorariosMf;
    },
    getGastoOtorgamiento(item) {
      const gastoOtorg = item.gastos && item.gastos.otorgamiento ? parseFloat(item.gastos.otorgamiento) : 0;
      const gastosEscribano = item.gastos && item.gastos.gastos_escribano ? parseFloat(item.gastos.gastos_escribano) : 0;
      return gastoOtorg + gastosEscribano;
    },
    getGastoIibb(item) {
      const gastoIibb = item.gastos && item.gastos.iibb ? parseFloat(item.gastos.iibb) : 0;
      const otrosGastos = item.gastos && item.gastos.otros_gastos ? parseFloat(item.gastos.otros_gastos) : 0;
      return gastoIibb + otrosGastos;
    },
    // Métodos para calcular subtotales de gastos
    getSubtotalGastoHonorariosMf(subtotalItem) {
      // Primero intentar usar el valor del backend
      if (subtotalItem._subtotales && subtotalItem._subtotales.gastos && subtotalItem._subtotales.gastos.honorarios_mf) {
        return parseFloat(subtotalItem._subtotales.gastos.honorarios_mf) || 0;
      }

      // Si no existe, calcular sumando items de la operatoria
      let operatoriaNombre = '';
      if (subtotalItem._subtotales && subtotalItem._subtotales.operatoria) {
        operatoriaNombre = subtotalItem._subtotales.operatoria;
      } else if (subtotalItem.operatoria) {
        operatoriaNombre = subtotalItem.operatoria.replace('Subtotal ', '');
      }

      return this.resultados
        .filter(item => !item._esSubtotal && item.operatoria === operatoriaNombre)
        .reduce((total, item) => {
          const honorariosMf = item.gastos && item.gastos.honorarios_mf ? parseFloat(item.gastos.honorarios_mf) : 0;
          return total + honorariosMf;
        }, 0);
    },
    getSubtotalGastoEscribano(subtotalItem) {
      // Primero intentar usar el valor del backend
      if (subtotalItem._subtotales && subtotalItem._subtotales.gastos && subtotalItem._subtotales.gastos.gastos_escribano) {
        return parseFloat(subtotalItem._subtotales.gastos.gastos_escribano) || 0;
      }

      // Si no existe, calcular sumando items de la operatoria
      let operatoriaNombre = '';
      if (subtotalItem._subtotales && subtotalItem._subtotales.operatoria) {
        operatoriaNombre = subtotalItem._subtotales.operatoria;
      } else if (subtotalItem.operatoria) {
        operatoriaNombre = subtotalItem.operatoria.replace('Subtotal ', '');
      }

      return this.resultados
        .filter(item => !item._esSubtotal && item.operatoria === operatoriaNombre)
        .reduce((total, item) => {
          const gastosEscribano = item.gastos && item.gastos.gastos_escribano ? parseFloat(item.gastos.gastos_escribano) : 0;
          return total + gastosEscribano;
        }, 0);
    },
    getSubtotalOtrosGastos(subtotalItem) {
      // Primero intentar usar el valor del backend
      if (subtotalItem._subtotales && subtotalItem._subtotales.gastos && subtotalItem._subtotales.gastos.otros_gastos) {
        return parseFloat(subtotalItem._subtotales.gastos.otros_gastos) || 0;
      }

      // Si no existe, calcular sumando items de la operatoria
      let operatoriaNombre = '';
      if (subtotalItem._subtotales && subtotalItem._subtotales.operatoria) {
        operatoriaNombre = subtotalItem._subtotales.operatoria;
      } else if (subtotalItem.operatoria) {
        operatoriaNombre = subtotalItem.operatoria.replace('Subtotal ', '');
      }

      return this.resultados
        .filter(item => !item._esSubtotal && item.operatoria === operatoriaNombre)
        .reduce((total, item) => {
          const otrosGastos = item.gastos && item.gastos.otros_gastos ? parseFloat(item.gastos.otros_gastos) : 0;
          return total + otrosGastos;
        }, 0);
    },
  },
};
</script>

<style scoped>
.subtotal-row {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
  border-top: 2px solid #007bff !important;
  border-bottom: 2px solid #007bff !important;
  border-left: 4px solid #007bff !important;
  font-weight: 600 !important;
  box-shadow: 0 2px 4px rgba(0,123,255,0.1) !important;
}

.subtotal-row:hover {
  background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%) !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,123,255,0.15) !important;
  transition: all 0.2s ease-in-out;
}

.subtotal-label {
  font-weight: 700;
  color: #007bff;
  font-size: 0.9em;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.subtotal-value {
  font-weight: 600;
  color: #28a745;
  font-size: 0.95em;
}

.desembolso-column {
  text-align: center;
}

.desembolso-monto {
  font-size: 0.9em;
}

.desembolso-fecha {
  font-size: 0.8em;
  color: #6c757d;
}

.legajo-link {
  color: #1976d2;
  text-decoration: none;
  font-weight: 500;
}

.legajo-link:hover {
  color: #1565c0;
  text-decoration: underline;
}
</style>
